import { hash } from 'ohash'
import { contractField, settledAnswer } from './contract'
import type { DefinitionSnapshot } from './versions'
import { nodeItemId } from './types'

/* "What changed" never re-derives from the activity trail: it diffs two
   definition snapshots directly, so the answer is exactly what an approval
   would have seen differently, nothing more and nothing less. Whether an
   item changed is decided on its whole value, hashed the way the
   fingerprint hashes it; the `before`/`after` text is only how it reads. */

const same = (a: unknown, b: unknown): boolean => hash(a) === hash(b)

/* An item listed as changed must read differently on each side, or the
   reviewer can't see what to re-approve: when two values' summaries match,
   show the values themselves. */
const readable = (
  beforeText: string,
  afterText: string,
  before: unknown,
  after: unknown,
): Pick<DefinitionChange, 'before' | 'after'> =>
  beforeText === afterText
    ? {
        before: JSON.stringify(before ?? null),
        after: JSON.stringify(after ?? null),
      }
    : { before: beforeText, after: afterText }

const citationsOf = (snapshot: DefinitionSnapshot, fieldId: string) =>
  snapshot.citations
    .filter((c) => c.fieldId === fieldId)
    .sort((a, b) => a.id.localeCompare(b.id))

const scopeValue = (snapshot: DefinitionSnapshot, fieldId: string) => ({
  answer: snapshot.answers[fieldId],
  notAsked: snapshot.notAsked[fieldId],
  pulledIn: snapshot.pulledIn[fieldId],
  citations: citationsOf(snapshot, fieldId),
})

const scopeText = (
  fieldId: string,
  value: ReturnType<typeof scopeValue>,
): string =>
  [
    fieldText(fieldId, value.answer),
    value.notAsked && `Not asked: ${value.notAsked.reason}`,
    value.pulledIn && `Pulled in by ${value.pulledIn.by}`,
    ...value.citations.map(
      (c) => `Cited “${c.text}”${c.cite ? ` (${c.cite})` : ''}`,
    ),
  ]
    .filter(Boolean)
    .join(' · ')

export interface DefinitionChange {
  area: 'scope' | 'design'
  itemId: string
  label: string
  kind: 'added' | 'removed' | 'changed'
  before: string
  after: string
}

const fieldText = (fieldId: string, value: unknown): string => {
  const field = contractField(fieldId)
  if (!field) return '(empty)'
  return settledAnswer(field, value as never).text
}

const scopeFieldChanges = (
  before: DefinitionSnapshot,
  after: DefinitionSnapshot,
): DefinitionChange[] => {
  const ids = new Set([
    ...Object.keys(before.answers),
    ...Object.keys(after.answers),
    ...Object.keys(before.notAsked),
    ...Object.keys(after.notAsked),
    ...Object.keys(before.pulledIn),
    ...Object.keys(after.pulledIn),
    ...before.citations.map((c) => c.fieldId),
    ...after.citations.map((c) => c.fieldId),
  ])
  return [...ids].flatMap((id) => {
    const b = scopeValue(before, id)
    const a = scopeValue(after, id)
    if (same(b, a)) return []
    const field = contractField(id)
    return [
      {
        area: 'scope' as const,
        itemId: id,
        label: field?.label ?? id,
        kind: 'changed' as const,
        ...readable(scopeText(id, b), scopeText(id, a), b, a),
      },
    ]
  })
}

const nodeText = (node: DefinitionSnapshot['nodes'][number]): string =>
  `${node.label} · ${node.predicate} · yes→${node.yesPath} no→${node.noPath}`

const insightText = (insight: DefinitionSnapshot['insights'][number]): string =>
  `${insight.label} · ${insight.insightType ?? ''} · ${insight.editType?.edit_type ?? ''} · ${insight.reasonCodeLevel1 ?? ''}/${insight.reasonCodeLevel2 ?? ''} · ${insight.insightText}`

const byId = <T extends { id: string }>(list: T[]): Map<string, T> =>
  new Map(list.map((entry) => [entry.id, entry]))

const nodeAndInsightChanges = (
  before: DefinitionSnapshot,
  after: DefinitionSnapshot,
): DefinitionChange[] => {
  const beforeNodes = byId(before.nodes)
  const afterNodes = byId(after.nodes)
  const nodeIds = new Set([...beforeNodes.keys(), ...afterNodes.keys()])
  const nodeChanges = [...nodeIds].flatMap((id) => {
    const b = beforeNodes.get(id)
    const a = afterNodes.get(id)
    if (b && a && nodeText(b) === nodeText(a)) return []
    return [
      {
        area: 'design' as const,
        itemId: nodeItemId(id),
        label: (a ?? b)!.label,
        kind: (!b ? 'added' : !a ? 'removed' : 'changed') as
          | 'added'
          | 'removed'
          | 'changed',
        before: b ? nodeText(b) : '',
        after: a ? nodeText(a) : '',
      },
    ]
  })

  const beforeInsights = byId(before.insights)
  const afterInsights = byId(after.insights)
  const insightIds = new Set([
    ...beforeInsights.keys(),
    ...afterInsights.keys(),
  ])
  const insightChanges = [...insightIds].flatMap((id) => {
    const b = beforeInsights.get(id)
    const a = afterInsights.get(id)
    if (b && a && same(b, a)) return []
    return [
      {
        area: 'design' as const,
        itemId: nodeItemId(id),
        label: (a ?? b)!.label,
        kind: (!b ? 'added' : !a ? 'removed' : 'changed') as
          | 'added'
          | 'removed'
          | 'changed',
        ...readable(b ? insightText(b) : '', a ? insightText(a) : '', b, a),
      },
    ]
  })
  return [...nodeChanges, ...insightChanges]
}

const codeListText = (list: DefinitionSnapshot['codeLists'][string]): string =>
  `${list.name} · ${list.system} · ${list.codes.join(', ') || 'no codes'} · on ${list.nodeIds.length} node${list.nodeIds.length === 1 ? '' : 's'}`

const codeListChanges = (
  before: DefinitionSnapshot,
  after: DefinitionSnapshot,
): DefinitionChange[] => {
  const ids = new Set([
    ...Object.keys(before.codeLists),
    ...Object.keys(after.codeLists),
  ])
  return [...ids].flatMap((id) => {
    const b = before.codeLists[id]
    const a = after.codeLists[id]
    if (same(b, a)) return []
    return [
      {
        area: 'design' as const,
        itemId: id,
        label: (a ?? b)!.name,
        kind: (!b ? 'added' : !a ? 'removed' : 'changed') as
          | 'added'
          | 'removed'
          | 'changed',
        ...readable(b ? codeListText(b) : '', a ? codeListText(a) : '', b, a),
      },
    ]
  })
}

const listChange = (
  itemId: string,
  label: string,
  before: unknown,
  after: unknown,
): DefinitionChange[] =>
  same(before, after)
    ? []
    : [
        {
          area: 'design' as const,
          itemId,
          label,
          kind: 'changed' as const,
          before: JSON.stringify(before),
          after: JSON.stringify(after),
        },
      ]

const sourceChanges = (
  before: DefinitionSnapshot,
  after: DefinitionSnapshot,
): DefinitionChange[] => {
  const beforeSources = byId(before.sources)
  const afterSources = byId(after.sources)
  const ids = new Set([...beforeSources.keys(), ...afterSources.keys()])
  return [...ids].flatMap((id) => {
    const b = beforeSources.get(id)
    const a = afterSources.get(id)
    if (same(b, a)) return []
    return [
      {
        area: 'scope' as const,
        itemId: id,
        label: (a ?? b)!.title,
        kind: (!b ? 'added' : !a ? 'removed' : 'changed') as
          | 'added'
          | 'removed'
          | 'changed',
        ...readable(b?.title ?? '', a?.title ?? '', b, a),
      },
    ]
  })
}

/**
 * Every item whose stored value differs between two definition snapshots —
 * exactly what a "what changed since vN" view shows. `[]` for identical
 * snapshots. Scope entries come first, then design.
 */
export const definitionChanges = (
  before: DefinitionSnapshot,
  after: DefinitionSnapshot,
): DefinitionChange[] => [
  ...scopeFieldChanges(before, after),
  ...sourceChanges(before, after),
  ...(before.description === after.description
    ? []
    : [
        {
          area: 'scope' as const,
          itemId: 'description',
          label: 'Description',
          kind: 'changed' as const,
          before: before.description || '(empty)',
          after: after.description || '(empty)',
        },
      ]),
  ...nodeAndInsightChanges(before, after),
  ...codeListChanges(before, after),
  ...listChange('design:metadata', 'Metadata', before.metadata, after.metadata),
  ...listChange('design:datasets', 'Datasets', before.datasets, after.datasets),
  ...listChange(
    'design:meta',
    'Design metadata',
    before.designMeta,
    after.designMeta,
  ),
  ...listChange(
    'design:external-docs',
    'External docs',
    before.externalDocs,
    after.externalDocs,
  ),
]
